import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import MuiLink from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { ArrowLeft, Wrench, Gauge, Clock, Car, User, Plus, X, Save } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../config/firebase';
import { VehicleService, VehicleServiceStatus } from '../../../../shared/types/vehicle-service.types';
import { Vehicle } from '../../../../shared/types/vehicle.types';
import { SettingListItem } from '../../../../shared/types/setting.types';
import useVehicle from '../../wrappers/use-vehicle';
import useVehicleService from '../../wrappers/use-vehicle-service';
import useSettingLists from '../../wrappers/use-setting-list-item';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import { useAuth } from '../../contexts/AuthContext';
import DocumentList from '../../components/DocumentList';
import { dealerServiceDetailStyles } from './ServiceDetail.styles';

const STATUS_CHIP: Record<VehicleServiceStatus, { color: 'default' | 'primary' | 'info' | 'warning' | 'success' | 'error' }> = {
    [VehicleServiceStatus.Ordered]:         { color: 'primary' },
    [VehicleServiceStatus.InProgress]:      { color: 'info' },
    [VehicleServiceStatus.WaitingForParts]: { color: 'warning' },
    [VehicleServiceStatus.Completed]:       { color: 'success' },
    [VehicleServiceStatus.Cancelled]:       { color: 'error' },
};

const infoRow = (label: string, value: React.ReactNode, icon?: React.ReactNode, mono?: boolean) => ({ label, value, icon, mono });

const DealerServiceDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const { user } = useAuth();
    const settingLists = useSettingLists();
    const { get: getVehicle } = useVehicle();
    const { get: getService, edit: editService } = useVehicleService();

    const [service, setService] = useState<VehicleService | null>(null);
    const [vehicle, setVehicle] = useState<Vehicle | null>(null);
    const [customerName, setCustomerName] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [serviceTypeMap, setServiceTypeMap] = useState<Record<string, string>>({});

    // Editable dealer fields
    const [plannedFinish, setPlannedFinish] = useState('');
    const [serviceTasks, setServiceTasks] = useState<string[]>([]);
    const [newTask, setNewTask] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [statusChanging, setStatusChanging] = useState(false);

    useEffect(() => {
        settingLists.list('serviceTypes').then((items) => {
            setServiceTypeMap(Object.fromEntries(items.map((i: SettingListItem) => [i.id, i.label])));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!id) return;
        const fetchData = async () => {
            const svc = await getService(id);
            if (!svc) {
                setNotFound(true);
                setLoading(false);
                return;
            }

            setService(svc);
            setPlannedFinish(svc.plannedFinish ?? '');
            setServiceTasks(svc.serviceTasks ?? []);

            const [vehicle, userSnap] = await Promise.all([
                getVehicle(svc.vehicleUid),
                getDoc(doc(firestore, 'user', svc.createdBy)),
            ]);

            if (vehicle) setVehicle(vehicle);
            if (userSnap.exists()) {
                setCustomerName(userSnap.data().name ?? null);
            }

            setLoading(false);
        };
        fetchData();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleAddTask = () => {
        const trimmed = newTask.trim();
        if (!trimmed) return;
        setServiceTasks((prev) => [...prev, trimmed]);
        setNewTask('');
    };

    const handleRemoveTask = (index: number) => {
        setServiceTasks((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (!id || !user) return;
        if (!plannedFinish) { setSaveError(t('dealerServiceDetail.saveDateError')); return; }
        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        try {
            await editService(id, {
                plannedFinish: plannedFinish || undefined,
                serviceTasks,
            });
            setSaveSuccess(true);
        } catch {
            setSaveError(t('dealerServiceDetail.saveError'));
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = async (newStatus: VehicleServiceStatus) => {
        if (!id || !user) return;
        setStatusChanging(true);
        try {
            await editService(id, {
                status: newStatus,
                ...(newStatus === VehicleServiceStatus.Completed && { finishedAt: new Date() }),
            });
            setService((prev) => prev ? {
                ...prev,
                status: newStatus,
                ...(newStatus === VehicleServiceStatus.Completed && { finishedAt: new Date() }),
            } : null);
        } finally {
            setStatusChanging(false);
        }
    };

    if (loading) {
        return (
            <Box sx={dealerServiceDetailStyles.loading}>
                <CircularProgress />
            </Box>
        );
    }

    if (notFound || !service) {
        return <Alert severity="error">{t('dealerServiceDetail.notFound')}</Alert>;
    }

    const serviceTypeLabel = serviceTypeMap[service.serviceType] ?? service.serviceType;
    const statusLabel = t(`vehicleServiceStatus.${service.status}`);
    const statusColor = STATUS_CHIP[service.status]?.color ?? 'default';

    const showProcessingSection = service.status !== VehicleServiceStatus.Ordered;
    const isReadOnly = service.status === VehicleServiceStatus.Completed || service.status === VehicleServiceStatus.Cancelled;

    const infoRows = [
        infoRow(t('dealerServiceDetail.serviceTypeLabel'), serviceTypeLabel),
        infoRow(t('dealerServiceDetail.receivedDate'), formatDate(service.createdAt)),
        infoRow(t('dealerServiceDetail.preferredDate'), service.preferredDate ? formatDate(service.preferredDate) : '—'),
        infoRow(t('dealerServiceDetail.plannedFinishLabel'), service.plannedFinish ? formatDate(service.plannedFinish) : '—'),
    ];

    const machineRows = [
        infoRow(t('dealerServiceDetail.modelLabel'), vehicle?.model ?? '—', <Car size={16} color="#757575" />),
        infoRow(t('dealerServiceDetail.vinLabel'), <MuiLink component={Link} to={`/app/service-desk/vehicles/${service.vehicleUid}`} fontFamily="monospace" variant="body2">{service.vehicleUid}</MuiLink>, <Car size={16} color="#757575" />),
        infoRow(t('dealerServiceDetail.kmLabel'), service.currentMileage !== undefined ? `${service.currentMileage} km` : '—', <Gauge size={16} color="#757575" />),
        infoRow(t('dealerServiceDetail.mthLabel'), service.currentHours !== undefined ? `${service.currentHours} h` : '—', <Clock size={16} color="#757575" />),
    ];

    const statusButtons: { status: VehicleServiceStatus; label: string; color: 'primary' | 'info' | 'warning' | 'success' | 'error'; visible: boolean }[] = [
        { status: VehicleServiceStatus.Ordered,         label: t('vehicleServiceStatus.ordered'),         color: 'primary', visible: service.status === VehicleServiceStatus.Cancelled },
        { status: VehicleServiceStatus.InProgress,      label: t('vehicleServiceStatus.inProgress'),      color: 'info',    visible: service.status !== VehicleServiceStatus.Completed },
        { status: VehicleServiceStatus.WaitingForParts, label: t('vehicleServiceStatus.waitingForParts'), color: 'warning', visible: service.status !== VehicleServiceStatus.Completed },
        { status: VehicleServiceStatus.Completed,       label: t('vehicleServiceStatus.completed'),       color: 'success', visible: service.status !== VehicleServiceStatus.Completed && service.status !== VehicleServiceStatus.Cancelled },
        { status: VehicleServiceStatus.Cancelled,       label: t('vehicleServiceStatus.cancelled'),       color: 'error',   visible: service.status !== VehicleServiceStatus.Completed && service.status !== VehicleServiceStatus.Cancelled },
    ];

    return (
        <Box sx={dealerServiceDetailStyles.root}>
            <Box sx={dealerServiceDetailStyles.header}>
                <Box sx={dealerServiceDetailStyles.headerMain}>
                    <IconButton component={Link} to="/app/current-services" size="small">
                        <ArrowLeft size={20} />
                    </IconButton>
                    <Typography variant="h4" fontWeight={700}>{t('dealerServiceDetail.title')}</Typography>
                    <Chip label={serviceTypeLabel} size="small" icon={<Wrench size={14} />} />
                    <Chip label={statusLabel} size="small" color={statusColor} />
                </Box>
                <Box sx={dealerServiceDetailStyles.headerActions}>
                    {statusButtons.filter((btn) => btn.visible).map((btn) => (
                        <Button
                            key={btn.status}
                            size="small"
                            variant={service.status === btn.status ? 'contained' : 'outlined'}
                            color={btn.color}
                            disabled={service.status === btn.status || statusChanging}
                            onClick={() => handleStatusChange(btn.status)}
                        >
                            {btn.label}
                        </Button>
                    ))}
                </Box>
            </Box>

            {customerName && (
                <Box sx={dealerServiceDetailStyles.customerRow}>
                    <User size={16} color="#757575" />
                    <Typography variant="body2" color="text.secondary">{t('dealerServiceDetail.customerLabel')}</Typography>
                    <Typography variant="body2" fontWeight={500}>{customerName}</Typography>
                </Box>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={dealerServiceDetailStyles.sectionPaper}>
                        <Box sx={dealerServiceDetailStyles.sectionTitle}>
                            <Typography variant="subtitle1" fontWeight={600}>{t('dealerServiceDetail.infoTitle')}</Typography>
                        </Box>
                        {infoRows.map((r, i, arr) => (
                            <Box key={r.label}>
                                <Box sx={dealerServiceDetailStyles.infoRow}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500}>{r.label}</Typography>
                                    <Typography variant="body2">{r.value}</Typography>
                                </Box>
                                {i < arr.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={dealerServiceDetailStyles.sectionPaper}>
                        <Box sx={dealerServiceDetailStyles.sectionTitle}>
                            <Typography variant="subtitle1" fontWeight={600}>{t('dealerServiceDetail.machineTitle')}</Typography>
                        </Box>
                        {machineRows.map((r, i, arr) => (
                            <Box key={r.label}>
                                <Box sx={dealerServiceDetailStyles.machineRow}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={500}>{r.label}</Typography>
                                    <Box sx={dealerServiceDetailStyles.machineValue}>
                                        {r.icon}
                                        <Typography variant="body2" fontFamily={r.mono ? 'monospace' : undefined}>{r.value as string}</Typography>
                                    </Box>
                                </Box>
                                {i < arr.length - 1 && <Divider />}
                            </Box>
                        ))}
                    </Paper>
                </Grid>
            </Grid>

            <Paper elevation={0} sx={dealerServiceDetailStyles.sectionPaper}>
                <Box sx={dealerServiceDetailStyles.sectionTitle}>
                    <Typography variant="subtitle1" fontWeight={600}>{t('dealerServiceDetail.descriptionTitle')}</Typography>
                </Box>
                <Box sx={dealerServiceDetailStyles.descriptionContent}>
                    <Typography variant="body2" color={service.description ? 'text.primary' : 'text.secondary'}>
                        {service.description || t('dealerServiceDetail.noDescription')}
                    </Typography>
                </Box>
            </Paper>

            {/* Dokumenty */}
            <Paper elevation={0} sx={dealerServiceDetailStyles.sectionPaper}>
                <DocumentList
                    sectionTitle={t('dealerServiceDetail.documentsTitle')}
                    filters={{ vehicleServiceUid: id, dealerId: user?.dealerId }}
                    uploadData={{ vehicleServiceUid: id, vehicleUid: service.vehicleUid, dealerId: user?.dealerId, garageUid: vehicle?.garageUid }}
                />
            </Paper>

            {/* Dealer editable section */}
            {showProcessingSection && (
                <Paper elevation={0} sx={dealerServiceDetailStyles.sectionPaper}>
                    <Box sx={dealerServiceDetailStyles.sectionTitle}>
                        <Typography variant="subtitle1" fontWeight={600}>{t('dealerServiceDetail.processingTitle')}</Typography>
                    </Box>
                    <Box sx={dealerServiceDetailStyles.processingContent}>

                        <Box sx={dealerServiceDetailStyles.plannedFinishRow}>
                            <Typography variant="body2" fontWeight={500} sx={dealerServiceDetailStyles.rowLabel}>{t('dealerServiceDetail.plannedFinishEditable')}</Typography>
                            {isReadOnly ? (
                                <Typography variant="body2">
                                    {plannedFinish ? formatDate(plannedFinish) : '—'}
                                </Typography>
                            ) : (
                                <TextField
                                    type="date"
                                    size="small"
                                    value={plannedFinish}
                                    onChange={(e) => { setPlannedFinish(e.target.value); setSaveSuccess(false); setSaveError(null); }}
                                    InputLabelProps={{ shrink: true }}
                                    error={!plannedFinish && !!saveError}
                                    sx={dealerServiceDetailStyles.dateInput}
                                />
                            )}
                        </Box>

                        {service.status === VehicleServiceStatus.Completed && (
                            <Box sx={dealerServiceDetailStyles.plannedFinishRow}>
                                <Typography variant="body2" fontWeight={500} sx={dealerServiceDetailStyles.rowLabel}>{t('dealerServiceDetail.completedLabel')}</Typography>
                                <Typography variant="body2">
                                    {service.finishedAt ? formatDate(service.finishedAt) : '—'}
                                </Typography>
                            </Box>
                        )}

                        <Divider />

                        <Box>
                            <Typography variant="body2" fontWeight={500} mb={1.5}>{t('dealerServiceDetail.tasksTitle')}</Typography>
                            {serviceTasks.length > 0 ? (
                                <List dense disablePadding sx={dealerServiceDetailStyles.tasksList}>
                                    {serviceTasks.map((task, i) => (
                                        <React.Fragment key={i}>
                                            {i > 0 && <Divider />}
                                            <ListItem
                                                secondaryAction={!isReadOnly ? (
                                                    <IconButton edge="end" size="small" onClick={() => handleRemoveTask(i)}>
                                                        <X size={16} />
                                                    </IconButton>
                                                ) : undefined}
                                                sx={dealerServiceDetailStyles.taskItem}
                                            >
                                                <ListItemText primary={task} primaryTypographyProps={{ variant: 'body2' }} />
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary" mb={1.5}>{t('dealerServiceDetail.noTasks')}</Typography>
                            )}
                            {!isReadOnly && (
                                <Box sx={dealerServiceDetailStyles.addTaskRow}>
                                    <TextField
                                        size="small"
                                        placeholder={t('dealerServiceDetail.addTaskPlaceholder')}
                                        value={newTask}
                                        onChange={(e) => setNewTask(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTask(); } }}
                                        sx={dealerServiceDetailStyles.addTaskInput}
                                    />
                                    <IconButton onClick={handleAddTask} disabled={!newTask.trim()} size="small" color="primary">
                                        <Plus size={20} />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>

                        {!isReadOnly && (
                            <>
                                {saveError && <Alert severity="error">{saveError}</Alert>}
                                {saveSuccess && <Alert severity="success">{t('dealerServiceDetail.saveSuccess')}</Alert>}

                                <Box>
                                    <Button
                                        variant="contained"
                                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                                        onClick={handleSave}
                                        disabled={saving}
                                    >
                                        {saving ? t('dealerServiceDetail.saving') : t('dealerServiceDetail.save')}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default DealerServiceDetail;
